import * as React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';
import { range, random, normalize } from '@/utils';
import useInterval from '@/hooks/use-interval';

import Demo, { Props as DemoProps } from '@/components/Demo';
import VisuallyHidden from '@/components/VisuallyHidden';
import { BaseWrapper as AsideWrapper } from '@/components/Aside';

import Heart from './Heart';
import ColorTable from './ColorTable';
import { formatForCss } from './ColorfulParticlesDemo.helpers';
import {
  Variant,
  Color,
  SampledParticleColors,
} from './ColorfulParticlesDemo.types';
import PopCircle from './PopCircle';
import ClickMe from './ClickMe';

interface Props extends DemoProps {
  variant: Variant;
  includeClickMe?: boolean;
}

type Particle = {
  id: string;
  angle: number;
  distance: number;
  fadeDuration: number;
  fadeDelay: number;
  popDuration: number;
  size: number;
  createdAt: number;
  fromColor: Color;
  toColor: Color;
  toFilter?: string;
};

const MIN_DISTANCE = 32;
const MAX_DISTANCE = 64;
const MIN_FADE_DURATION = 1000;
const MAX_FADE_DURATION = 2000;
const MAX_FADE_DELAY = 500;
const MAX_FADE_ADJUST = 200;
const PARTICLE_DELAY = 150;

const NUM_OF_PARTICLES = [
  2, 3, 5, 8, 13, 21, 34, 55, 89, 144, 233, 377, 610,
];

function ColorfulParticlesDemo({
  variant,
  includeClickMe,
  ...delegated
}: Props) {
  const [isLiked, setIsLiked] = React.useState(false);
  const [particles, setParticles] = React.useState<Particle[]>([]);
  const [generatedColorSample, setGeneratedColorSample] =
    React.useState<Array<SampledParticleColors>>([]);

  const [showClickMe, setShowClickMe] =
    React.useState(!!includeClickMe);

  useInterval(() => {
    setParticles(
      particles.filter((particle) => {
        return Date.now() - particle.createdAt < 3000;
      })
    );
  }, 3000);

  return (
    <StyledDemo
      {...delegated}
      initialValues={{
        particleQuantity: 4,
      }}
      outputWrapper={null}
      controlPosition="top"
      customGrid={{
        gridTemplateColumns: 'min(400px, 100%)',
        justifyContent: 'center',
      }}
      controls={[]}
    >
      {({ particleQuantity }) => {
        const numOfParticles = NUM_OF_PARTICLES[particleQuantity];

        return (
          <Wrapper data-can-be-annotated={false}>
            <TableWrapper>
              <ColorTable
                colors={generatedColorSample}
                variant={variant}
              />
            </TableWrapper>
            <ButtonWrapper>
              <Button
                data-is-liked={isLiked}
                data-variant={variant}
                style={{
                  '--pop-circle-duration': `${PARTICLE_DELAY}ms`,
                }}
                onClick={() => {
                  if (isLiked) {
                    setIsLiked(false);
                    return;
                  }
                  setIsLiked(true);

                  setShowClickMe(false);

                  const newParticles = generateParticles(
                    numOfParticles,
                    variant
                  );

                  setGeneratedColorSample(
                    newParticles.slice(0, 6).map((particle) => ({
                      fromColor: particle.fromColor,
                      toColor: particle.toColor,
                    }))
                  );

                  window.setTimeout(() => {
                    setParticles([...particles, ...newParticles]);
                  }, PARTICLE_DELAY);
                }}
              >
                <PopCircle isPopped={isLiked} />
                <Heart data-is-heart="true" size={48} />
                <VisuallyHidden>Like this post</VisuallyHidden>

                {particles.map((particle) => {
                  return (
                    <Particle
                      key={particle.id}
                      data-variant={variant}
                      style={{
                        '--angle': particle.angle + 'deg',
                        '--distance': particle.distance + 'px',
                        '--size': particle.size + 'px',
                        '--fade-duration':
                          particle.fadeDuration + 'ms',
                        '--fade-delay': particle.fadeDelay + 'ms',
                        '--pop-duration': particle.popDuration + 'ms',
                        '--from-color': formatForCss(
                          particle.fromColor
                        ),
                        '--to-color': formatForCss(particle.toColor),
                        '--to-filter': particle.toFilter,
                      }}
                    />
                  );
                })}
              </Button>
              {includeClickMe && <ClickMe isVisible={showClickMe} />}
            </ButtonWrapper>
          </Wrapper>
        );
      }}
    </StyledDemo>
  );
}

function generateParticles(
  numOfParticles: number,
  variant: Variant
): Array<Particle> {
  return range(numOfParticles).map((index) => {
    const distance = random(MIN_DISTANCE, MAX_DISTANCE);

    const symmetricalAngle = normalize(
      index,
      0,
      numOfParticles,
      0,
      360
    );
    const angleJitter = 40;
    const angle =
      symmetricalAngle + random(-angleJitter, angleJitter);

    const popDuration =
      normalize(distance, MIN_DISTANCE, MAX_DISTANCE, 400, 800) +
      random(-200, 200);

    const fadeDuration =
      normalize(
        distance,
        MIN_DISTANCE,
        MAX_DISTANCE,
        MIN_FADE_DURATION,
        MAX_FADE_DURATION
      ) + random(-200, 200);
    const fadeDelay =
      normalize(
        distance,
        MIN_DISTANCE,
        MAX_DISTANCE,
        0,
        MAX_FADE_DELAY
      ) + random(0, MAX_FADE_ADJUST);

    let fromColor: Color, toColor: Color;

    switch (variant) {
      case 'basic-rgb': {
        fromColor = {
          type: 'rgb' as const,
          id: crypto.randomUUID(),
          values: {
            r: random(0, 256),
            g: random(0, 256),
            b: random(0, 256),
          },
        };

        toColor = {
          ...fromColor,
          id: crypto.randomUUID(),
        };

        break;
      }

      case 'basic-hsl': {
        fromColor = {
          type: 'hsl' as const,
          id: crypto.randomUUID(),
          values: {
            h: random(0, 360),
            s: 100,
            l: 80,
          },
        };

        toColor = {
          ...fromColor,
          id: crypto.randomUUID(),
        };

        break;
      }

      case 'basic-oklch': {
        fromColor = {
          type: 'oklch' as const,
          id: crypto.randomUUID(),
          values: {
            l: 0.8,
            c: 0.1,
            h: random(0, 360),
          },
        };

        toColor = {
          ...fromColor,
          id: crypto.randomUUID(),
        };

        break;
      }

      case 'filter-flat': {
        fromColor = {
          type: 'hsl' as const,
          id: crypto.randomUUID(),
          values: {
            h: random(0, 360),
            s: 80,
            l: 80,
          },
        };

        toColor = {
          ...fromColor,
          id: crypto.randomUUID(),
        };

        break;
      }

      case 'hsl-plus-180': {
        fromColor = {
          type: 'hsl' as const,
          id: crypto.randomUUID(),
          values: {
            h: random(0, 360),
            s: 100,
            l: 80,
          },
        };

        toColor = {
          type: 'hsl' as const,
          id: crypto.randomUUID(),
          values: {
            ...fromColor.values,
            h: fromColor.values.h + 180,
          },
        };

        break;
      }

      default: {
        throw new Error(`Unknown variant: ${variant}`);
      }
    }

    const particle: Particle = {
      id: crypto.randomUUID(),
      angle,
      distance,
      size: random(9, 15),
      createdAt: Date.now(),
      popDuration,
      fadeDuration,
      fadeDelay,
      fromColor,
      toColor,
    };

    if (variant === 'filter-flat') {
      particle.toFilter = `hue-rotate(720deg)`;
    }

    return particle;
  });
}

const StyledDemo = styled(Demo)`
  margin-inline: -32px;

  ${AsideWrapper} & {
    margin-inline: -16px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap-reverse;
  gap: 24px;
  padding-inline: 1rem;
  border-radius: 16px;
`;

const TableWrapper = styled.div`
  width: 100%;
  min-width: 0;
  margin-inline: 4px;

  ${AsideWrapper} & {
    margin-inline: 0;
  }

  @media ${BREAKPOINTS.smAndLarger} {
    flex: 1;
    min-width: 28.5rem;
    margin-inline: 0;
  }
`;

const ButtonWrapper = styled.div`
  flex: 1;
  position: relative;
  min-width: 116px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Button = styled.button`
  --red: oklch(0.65 0.3 19.41);
  position: relative;
  padding: 32px;
  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;

  &:hover {
    background: hsl(0deg 0% 100% / 0.1);

    path {
      stroke: var(--red);
    }
  }

  svg[data-is-heart='true'] {
    display: block;
  }

  &[data-is-liked='true'] {
    svg[data-is-heart='true'] {
      animation: fromShrunken 2000ms 200ms backwards
        linear(
          0,
          0.04 1.1%,
          0.156 2.3%,
          1.015 8.5%,
          1.157 10.4%,
          1.217 12.4%,
          1.217 13.6%,
          1.193 15%,
          0.992 21.7%,
          0.964 23.5%,
          0.952 25.3%,
          0.957 27.9%,
          1.002 34.7%,
          1.01 38.2%,
          0.998 51%,
          1
        );

      path {
        fill: var(--red);
        stroke: var(--red);
      }
    }
  }
`;

const Particle = React.memo(styled.span`
  --particle-curve: cubic-bezier(0.2, 0.56, 0, 1);
  position: absolute;
  inset: 0;
  margin: auto;
  width: var(--size);
  height: var(--size);
  border-radius: 50%;
  pointer-events: none;
  background: var(--to-color);
  transform: translate(
    calc(var(--distance) * cos(var(--angle))),
    calc(var(--distance) * sin(var(--angle)))
  );
  animation:
    fadeToTransparent var(--fade-duration) var(--fade-delay) forwards,
    fromRestingPosition var(--pop-duration) var(--particle-curve);

  @keyframes toFilter {
    to {
      filter: var(--to-filter);
    }
  }

  @keyframes colorShift {
    from {
      background: var(--from-color);
    }
  }

  &[data-variant='hsl-plus-180'] {
    animation:
      fadeToTransparent var(--fade-duration) var(--fade-delay)
        forwards,
      fromRestingPosition var(--pop-duration) var(--particle-curve),
      colorShift 1000ms linear forwards;
  }
  &[data-variant='filter-flat'] {
    animation:
      fadeToTransparent var(--fade-duration) var(--fade-delay)
        forwards,
      fromRestingPosition var(--pop-duration) var(--particle-curve),
      toFilter 1000ms linear forwards;
  }
`);

export default ColorfulParticlesDemo;
